import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";

import { Section, SectionHeader } from "@/components/site/Section";
import { ConsentGate } from "@/components/site/ConsentGate";
import { QuestionDeck } from "@/components/site/QuestionDeck";
import { ListenControl } from "@/components/site/ListenControl";
import { ThemeSummary } from "@/components/site/ThemeSummary";
import { getCourse } from "@/content/courses";
import { questionsFor, type WatchPartySummary } from "@/content/watch-party";
import {
  endWatchParty,
  getWatchParty,
  summarizeDiscussion,
  type WatchParty,
} from "@/utils/watch-party.functions";

export const Route = createFileRoute("/_authenticated/watch-party/$partyId/")({
  head: () => ({
    meta: [
      { title: "Running a watch party | Leadership Edge Live" },
      {
        name: "description",
        content: "Work through the discussion questions one at a time with your team.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Running a watch party" },
      { property: "og:description", content: "Discussion questions, one at a time." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RunWatchParty,
});

function RunWatchParty() {
  const { partyId } = Route.useParams();
  const load = useServerFn(getWatchParty);
  const summarize = useServerFn(summarizeDiscussion);
  const close = useServerFn(endWatchParty);
  const navigate = useNavigate();

  const [party, setParty] = useState<WatchParty | null>(null);
  const [summaries, setSummaries] = useState<Record<number, WatchPartySummary>>({});
  const [consented, setConsented] = useState(false);
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    load({ data: { partyId } })
      .then(({ party: p, summaries: s }) => {
        setParty(p);
        setSummaries(Object.fromEntries(s.map((row) => [row.questionIndex, row])));
      })
      .catch(() => setLoadError(true));
  }, [load, partyId]);

  const questions = useMemo(
    () => (party ? questionsFor(party.courseSlug, party.sessionIndex) : []),
    [party],
  );

  function runSummary(text: string) {
    setBusy(true);
    setError(null);
    summarize({ data: { partyId, questionIndex: index, text } })
      .then((summary) => setSummaries((prev) => ({ ...prev, [index]: summary })))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "We couldn't summarize that discussion."),
      )
      .finally(() => setBusy(false));
  }

  function finish() {
    close({ data: { partyId } })
      .catch(() => undefined)
      .finally(() => navigate({ to: "/watch-party/$partyId/recap/", params: { partyId } }));
  }

  if (loadError) {
    return (
      <Section>
        <SectionHeader
          title="We couldn't find that watch party"
          intro="It may have been removed, or it belongs to someone else."
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
        />
        <Link
          to="/watch-party/"
          className="rounded-[6px] bg-[#693D94] px-6 py-4 text-base font-semibold text-primary-foreground shadow-brand"
        >
          Back to watch parties
        </Link>
      </Section>
    );
  }

  if (!party) {
    return (
      <Section>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </Section>
    );
  }

  const course = getCourse(party.courseSlug);

  if (questions.length === 0) {
    return (
      <Section>
        <SectionHeader
          eyebrow="Watch Party"
          title="Discussion questions coming soon for this course"
          intro="We write these course by course so the questions are worth reading aloud. This one isn't ready yet."
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
        />
        <Link
          to="/watch-party/"
          className="rounded-[6px] bg-[#693D94] px-6 py-4 text-base font-semibold text-primary-foreground shadow-brand"
        >
          Pick another course
        </Link>
      </Section>
    );
  }

  const summary = summaries[index];

  return (
    <Section>
      <div className="mx-auto max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          {course?.title ?? party.courseSlug}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {party.attendeeCount} in the room
          {party.sessionIndex != null && course?.sessions?.[party.sessionIndex]
            ? ` · ${course.sessions[party.sessionIndex].title}`
            : ""}
        </p>

        <div className="mt-6">
          {!consented ? (
            <ConsentGate onConfirm={() => setConsented(true)} />
          ) : (
            <QuestionDeck
              question={questions[index]}
              index={index}
              total={questions.length}
              onPrevious={() => setIndex((i) => Math.max(0, i - 1))}
              onNext={() => (index === questions.length - 1 ? finish() : setIndex((i) => i + 1))}
            >
              <ListenControl questionIndex={index} busy={busy} onSummarize={runSummary} />

              {error && <p className="mt-4 text-sm text-destructive">{error}</p>}

              {summary && (
                <div className="mt-6 rounded-2xl border border-border/60 bg-background p-6">
                  <ThemeSummary summary={summary} />
                </div>
              )}
            </QuestionDeck>
          )}
        </div>
      </div>
    </Section>
  );
}
